// Picking a set of account cards the way the account card dashboard does: first narrow by pal code
// (one, a range of them, or a whole type), then take all of what is left, a range of cards or a hand-picked list.

export const PAL_CODE_TYPE_SORT_ORDER = {
    'מאזני': 0,
    'מאזני רכוש קבוע': 1,
    'תוצאתי': 2,
};

/** Pal codes in reporting order: by type first, then by their own order number. */
export const sortPalCodeOptions = (palCodeOptions = []) =>
    [...palCodeOptions].sort((a, b) => {
        const typeA = PAL_CODE_TYPE_SORT_ORDER[a.palCodeType] ?? 999;
        const typeB = PAL_CODE_TYPE_SORT_ORDER[b.palCodeType] ?? 999;
        if (typeA !== typeB) return typeA - typeB;
        return (a.orderNumber ?? 9999) - (b.orderNumber ?? 9999);
    });

export const palCodeOrderMapOf = (sortedPalCodeOptions = []) =>
    sortedPalCodeOptions.reduce((map, pal, index) => {
        map[pal.value] = index;
        return map;
    }, {});

/** The pal codes between two of them, in reporting order and whichever way round they were picked. */
export const palCodesBetween = (sortedPalCodeOptions = [], fromPalCode, toPalCode) => {
    const fromIndex = sortedPalCodeOptions.findIndex((pal) => pal.value === fromPalCode?.value);
    const toIndex = sortedPalCodeOptions.findIndex((pal) => pal.value === toPalCode?.value);
    if (fromIndex === -1 || toIndex === -1) return null;
    return sortedPalCodeOptions
        .slice(Math.min(fromIndex, toIndex), Math.max(fromIndex, toIndex) + 1)
        .map((pal) => pal.value);
};

/** Cards in reporting order: by the pal code section they belong to, then by card number. */
export const sortCardsByPalCode = (cards = [], palCodeOrderMap = {}) =>
    [...cards].sort((a, b) => {
        const palA = palCodeOrderMap[a.palCode] ?? 9999;
        const palB = palCodeOrderMap[b.palCode] ?? 9999;
        if (palA !== palB) return palA - palB;
        const idA = parseInt(a.cardId, 10);
        const idB = parseInt(b.cardId, 10);
        if (!Number.isNaN(idA) && !Number.isNaN(idB)) return idA - idB;
        return String(a.cardId).localeCompare(String(b.cardId));
    });

// A card with no pal code of its own belongs to the "NonPalCode" bucket, which is itself a pal code option.
const palCodeOf = (card) => (card.palCode && card.palCode.trim() !== '' ? card.palCode : 'NonPalCode');

/**
 * The cards a pal-code filter leaves: `mode` is 'all', 'single' (one or more picked), 'range'
 * (everything between two of them) or 'type' (a whole pal code type).
 */
export const filterCardsByPalCode = ({
    cards = [],
    sortedPalCodeOptions = [],
    mode = 'all',
    selectedPalCodes = [],
    fromPalCode = null,
    toPalCode = null,
    palCodeType = null,
}) => {
    if (mode === 'type' && palCodeType?.value) {
        const ofType = sortedPalCodeOptions
            .filter((pal) => pal.palCodeType === palCodeType.value)
            .map((pal) => pal.value);
        return cards.filter((card) => ofType.includes(palCodeOf(card)));
    }

    if (mode === 'range') {
        const inRange = palCodesBetween(sortedPalCodeOptions, fromPalCode, toPalCode);
        return inRange ? cards.filter((card) => inRange.includes(palCodeOf(card))) : cards;
    }

    if (mode === 'single' && selectedPalCodes.length > 0) {
        const picked = selectedPalCodes.map((pal) => pal.value);
        return cards.filter((card) => picked.includes(palCodeOf(card)));
    }

    return cards;
};

/** The cards between two of them, in the order they are listed. */
export const cardsBetween = (cards = [], fromCard, toCard) => {
    const fromIndex = cards.findIndex((card) => card.cardId === fromCard?.cardId);
    const toIndex = cards.findIndex((card) => card.cardId === toCard?.cardId);
    if (fromIndex === -1 || toIndex === -1) return [];
    return cards.slice(Math.min(fromIndex, toIndex), Math.max(fromIndex, toIndex) + 1);
};

/** The pal code types present in a set of pal codes, in reporting order. */
export const palCodeTypesOf = (palCodeOptions = []) =>
    [...new Set(sortPalCodeOptions(palCodeOptions).map((pal) => pal.palCodeType).filter(Boolean))];
